import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

/* navbot-components-parts-fallback start */
(() => {
  if (location.pathname !== '/collections/components-parts') return;
  const handles = ['video-transmission-screen-power-bank','nvidia-edge-computing-carrier-board','remote-controller','lora-gps-video-transmission-module','usb-to-can-adapter','d1-parts-screws-bearings'];
  const money = (cents) => '$' + (Number(cents || 0) / 100).toFixed(2);
  const esc = (text) => String(text || '').replace(/"/g, '&quot;');
  async function loadProduct(handle) { const r = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' }); if (!r.ok) throw new Error(handle); return r.json(); }
  function makeCard(product) {
    const url = product.url || `/products/${product.handle}`;
    const img = product.featured_image || (product.images && product.images[0]) || '';
    const title = product.title || product.handle;
    const el = document.createElement('article');
    el.className = 'navbot-extra-part-card';
    el.innerHTML = `<a class="navbot-extra-part-image" href="${url}" aria-label="${esc(title)}">${img ? `<img src="${img}" alt="${esc(title)}" loading="lazy">` : ''}</a><a class="navbot-extra-part-title" href="${url}">${title}</a><div class="navbot-extra-part-price">${money(product.price_min || product.price || 0)}</div>`;
    return el;
  }
  function addStyles() {
    if (document.getElementById('navbot-extra-parts-style')) return;
    const style = document.createElement('style');
    style.id = 'navbot-extra-parts-style';
    style.textContent = `.navbot-extra-parts{margin:48px auto 56px}.navbot-extra-parts h2{margin:0 0 24px;font-size:clamp(24px,2vw,34px);line-height:1.15}.navbot-extra-parts-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:28px 16px}.navbot-extra-part-card{min-width:0}.navbot-extra-part-image{display:block;aspect-ratio:1/1;background:#f6f6f6;overflow:hidden;margin-bottom:12px}.navbot-extra-part-image img{width:100%;height:100%;object-fit:cover;display:block}.navbot-extra-part-title{display:block;color:inherit;text-decoration:none;font-weight:600;line-height:1.35}.navbot-extra-part-title:hover{text-decoration:underline}.navbot-extra-part-price{margin-top:4px;font-weight:600;font-size:.95em}@media(max-width:990px){.navbot-extra-parts-grid{grid-template-columns:repeat(3,minmax(0,1fr))}}@media(max-width:640px){.navbot-extra-parts-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:22px 12px}}`;
    document.head.appendChild(style);
  }
  async function render() {
    if (document.body.innerText.includes('USB-to-CAN Adapter')) return;
    if (document.querySelector('[data-navbot-extra-parts]')) return;
    const products = (await Promise.allSettled(handles.map(loadProduct))).filter(x => x.status === 'fulfilled').map(x => x.value);
    if (!products.length) return;
    addStyles();
    const section = document.createElement('section');
    section.className = 'navbot-extra-parts page-width';
    section.setAttribute('data-navbot-extra-parts', 'true');
    section.innerHTML = '<h2>Additional Components & Parts</h2><div class="navbot-extra-parts-grid"></div>';
    products.forEach(p => section.querySelector('.navbot-extra-parts-grid').appendChild(makeCard(p)));
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    (footer && footer.parentNode ? footer.parentNode : document.body).insertBefore(section, footer || null);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', render, { once: true }); else render();
})();
/* navbot-components-parts-fallback end */